import { ListingFormData } from '../types/listing_onboarding';
import axiosClient from './axiosClient';

export const ROUTES = {
  listings: {
    create: '/listing/onboarding',
    createV2: '/listings/create_v2',
    generateDetails: '/api/v1/open_ai_api/generate_details',
    show: (id: string) => `/listings/${id}`,
  },
  pendingListings: {
    create: '/api/pending_listings',
    photos: (id: string) => `/api/pending_listings/${id}/photos`,
    presignedUrl: (id: string) => `/api/pending_listings/${id}/presigned_url`,
    createPhoto: (id: string) => `/api/pending_listings/${id}/photos`,
    updateFeatured: (id: string, photoId: string) => `/api/pending_listings/${id}/photos/${photoId}/featured`,
    deletePhoto: (id: string, photoId: string) => `/api/pending_listings/${id}/photos/${photoId}`,
  },
} as const;

interface CreateOptions {
  onSuccess?: (data: { listingId: string; aiEmail: string }) => void;
  onError?: (error: any) => void;
}

interface CreateV2Options {
  onSuccess?: (data: { listingId: string; aiEmail: string }) => void;
  onError?: (error: any) => void;
  pendingListingId?: string;
}

interface PhotoMetadata {
  photoId: string;
  fileName: string;
  isFeatured: boolean;
  uploadOrder: number;
  previewUrl: string;
}

interface PresignedUrlResponse {
  presignedUrl: string;
  photoId: string;
}

export const listingService = {
  create: async (formData: ListingFormData, pendingListingId: string, options?: CreateOptions) => {

    try {
      // Construct the URL with the pending_listing_id
      const url = `/listing/onboarding/${pendingListingId}`;

      const response = await axiosClient.post(url, {
        listing: formData
      });


      // Extract listingId and aiEmail from the response
      const { listingId, aiEmail } = response.data;

      options?.onSuccess?.({ listingId, aiEmail });
    } catch (error) {
      console.error('Error creating listing:', error);
      options?.onError?.(error);
    }
  },

  createPendingListing: async (): Promise<string> => {
    const response = await axiosClient.post(ROUTES.pendingListings.create, {})
    return response.data.id as string
  },

  createV2: async (formData: ListingFormData, options?: CreateV2Options) => {
    try {
      const body: Record<string, unknown> = {
        listing: formData,
        availability: formData.availability,
        timezone: formData.timezone,
        screening_criteria: {
          minimum_monthly_income: formData.minimumMonthlyIncome,
          accepts_students: formData.acceptsStudents === 'true',
          accepts_first_time_renters: formData.acceptsFirstTimeRenters === 'true',
          requires_1_year_of_employment: formData.requiresOneYearEmployment === 'true',
          requires_background_and_credit_check: formData.requiresBackgroundCreditCheck === 'true',
          cosigners_okay: true // Default value, can be added to form later
        }
      }
      if (options?.pendingListingId) {
        body.pending_listing_id = options.pendingListingId
      }
      const response = await axiosClient.post(ROUTES.listings.createV2, body);

      // Extract data from the response
      const { listingId, aiEmail } = response.data;

      options?.onSuccess?.({ listingId, aiEmail });
    } catch (error) {
      console.error('Error creating listing v2:', error);
      options?.onError?.(error);
    }
  },

  generateDetails: async (params: any) => {
    try {
      const response = await axiosClient.post(ROUTES.listings.generateDetails, params);
      return response.data;
    } catch (error) {
      console.error('Error generating details:', error);
      throw error;
    }
  },

  // Pending listing photo operations
  getPendingListingPhotos: async (pendingListingId: string): Promise<PhotoMetadata[]> => {
    try {
      const response = await axiosClient.get(`${ROUTES.pendingListings.photos(pendingListingId)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pending listing photos:', error);
      throw error;
    }
  },

  getPresignedUrl: async (pendingListingId: string): Promise<PresignedUrlResponse> => {
    try {
      const response = await axiosClient.post(`${ROUTES.pendingListings.presignedUrl(pendingListingId)}`);
      return response.data;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw error;
    }
  },

  uploadToS3: async (presignedUrl: string, file: File): Promise<void> => {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to upload file to S3');
      }
    } catch (error) {
      console.error('Error uploading to S3:', error);
      throw error;
    }
  },

  createPendingPhoto: async (pendingListingId: string, photoId: string, fileName: string): Promise<PhotoMetadata> => {
    try {
      const response = await axiosClient.post(`${ROUTES.pendingListings.createPhoto(pendingListingId)}`, {
        photo_id: photoId,
        file_name: fileName,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating pending photo record:', error);
      throw error;
    }
  },

  setFeaturedPhoto: async (pendingListingId: string, photoId: string): Promise<void> => {

    try {
      const response = await axiosClient.patch(`${ROUTES.pendingListings.updateFeatured(pendingListingId, photoId)}`);
    } catch (error) {
      console.error('Error setting featured photo:', error);
      throw error;
    }
  },

  deletePendingPhoto: async (pendingListingId: string, photoId: string): Promise<void> => {
    try {
      const response = await axiosClient.delete(`${ROUTES.pendingListings.deletePhoto(pendingListingId, photoId)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  },

  // Convenience method for complete photo upload flow
  uploadPhoto: async (pendingListingId: string, file: File): Promise<PhotoMetadata> => {
    try {
      // 1. Get presigned URL
      const { presignedUrl, photoId } = await listingService.getPresignedUrl(pendingListingId);

      // 2. Upload to S3
      await listingService.uploadToS3(presignedUrl, file);

      // 3. Create photo record
      const photoMetadata = await listingService.createPendingPhoto(pendingListingId, photoId, file.name);

      return photoMetadata;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  },
};